// packages/console/src/lib/livePlayback.ts
//
// Pure helpers shared by the live playback affordances (the ladder's Play/Copy
// buttons, the standalone player page, and App's route gate). Kept out of the
// component modules so those files export only components (react-refresh/fast-
// refresh stays happy) and so the parse logic is unit-testable without a DOM.

import type { LiveRung } from "../api/live";

// PLAY_PARAM is the query key carrying the minted ABR master URL to the
// standalone player tab; TITLE_PARAM optionally carries a human label.
export const PLAY_PARAM = "play";
export const TITLE_PARAM = "title";

// playbackUrlFromLocation reads and validates the master URL from a location
// query string. It returns null when absent or not an http(s) URL, so the page
// can show an honest empty state rather than feed junk to the player.
export function playbackUrlFromLocation(search: string): string | null {
  const raw = new URLSearchParams(search).get(PLAY_PARAM);
  if (!raw) return null;
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const parsed = new URL(raw, origin);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return raw;
  } catch {
    return null;
  }
}

// WHEP_PARAM carries the WHEP URL to the standalone WebRTC player tab (the AV1
// low-latency analogue of PLAY_PARAM).
export const WHEP_PARAM = "whep";

// whepUrlFromLocation reads and validates the WHEP URL from a query string. It
// accepts a same-origin path (/api/webrtc/…) or an absolute http(s) URL, so the
// standalone WebRTC page renders an honest empty state rather than feed junk to the
// peer connection.
export function whepUrlFromLocation(search: string): string | null {
  const raw = new URLSearchParams(search).get(WHEP_PARAM);
  if (!raw) return null;
  if (raw.startsWith("/")) return raw;
  try {
    const p = new URL(raw);
    if (p.protocol === "http:" || p.protocol === "https:") return raw;
  } catch {
    return null;
  }
  return null;
}

// firstActiveRungName returns the name of the FIRST enabled ("on") rung in ladder
// order, or null when none is enabled. This is the anchor row that carries the
// Play/Copy affordances: the highest rung the operator has turned on. Since
// Play/Copy target the ABR MASTER (not this rung), it is a stable anchor rather
// than a per-rung playback source.
export function firstActiveRungName(rungs: LiveRung[]): string | null {
  const active = rungs.find((r) => r.on);
  return active ? active.rungName : null;
}
